import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Table2, ChevronRight } from 'lucide-react';
import { formatCurrency, formatNumber } from '../utils/formatters';

export default function DataTable({
  data = [],
  columns = [],
  loading = false,
  emptyMessage = 'No data available',
  getRowId = (_, index) => index,
  isRowExpandable = () => false,
  renderExpandedContent = null,
  getExpandButtonLabel = null,
  rowClassName = () => '',
}) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState([]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    const sortColumn = columns.find((column) => column.key === sortConfig.key);

    return [...data].sort((a, b) => {
      const aValue = sortColumn?.sortValue ? sortColumn.sortValue(a[sortConfig.key], a) : a[sortConfig.key];
      const bValue = sortColumn?.sortValue ? sortColumn.sortValue(b[sortConfig.key], b) : b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aNumber = Number(aValue);
      const bNumber = Number(bValue);
      if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber) && aValue !== '' && bValue !== '') {
        return sortConfig.direction === 'asc' ? aNumber - bNumber : bNumber - aNumber;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr);
      }
      return bStr.localeCompare(aStr);
    });
  }, [data, sortConfig]);

  const formatValue = (value, format) => {
    if (value === null || value === undefined) return '-';
    switch (format) {
      case 'currency':
        return formatCurrency(value);
      case 'number':
        return formatNumber(value);
      case 'decimal':
        return formatNumber(value, 2);
      case 'percent':
        return `${value}%`;
      default:
        return value;
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-4 h-4 text-primary-600" />
    ) : (
      <ChevronDown className="w-4 h-4 text-primary-600" />
    );
  };

  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-14 bg-gray-50 border-t border-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  const toggleExpandedRow = (rowId) => {
    setExpandedRowIds((current) => (
      current.includes(rowId)
        ? current.filter((id) => id !== rowId)
        : [...current, rowId]
    ));
  };

  const hasExpandableRows = Boolean(renderExpandedContent);

  const table = (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {hasExpandableRows && (
              <th className="w-28 px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <span className="sr-only">Expand</span>
              </th>
            )}
            {columns.map((column) => {
              const isNumeric = ['number', 'currency', 'decimal', 'percent'].includes(column.format);
              return (
                <th
                  key={column.key}
                  className={`px-3 sm:px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors ${isNumeric ? 'text-right' : 'text-left'}`}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                >
                  <div className={`flex items-center gap-2 ${isNumeric ? 'justify-end' : ''}`}>
                    {column.label}
                    {column.sortable !== false && getSortIcon(column.key)}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedData.map((row, rowIndex) => {
            const rowId = getRowId(row, rowIndex);
            const expandable = hasExpandableRows && isRowExpandable(row);
            const expanded = expandable && expandedRowIds.includes(rowId);
            const expandLabel = getExpandButtonLabel?.(row, expanded);

            return (
              <React.Fragment key={rowId}>
                <tr className={`hover:bg-gray-50 transition-colors ${rowClassName(row)}`.trim()}>
                  {hasExpandableRows && (
                    <td className="px-3 sm:px-4 py-3 sm:py-4 align-top">
                      {expandable ? (
                        <button
                          type="button"
                          onClick={() => toggleExpandedRow(rowId)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50"
                          aria-label={expanded ? 'Collapse row details' : 'Expand row details'}
                        >
                          <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                          <span>{expandLabel || (expanded ? 'Hide details' : 'View details')}</span>
                        </button>
                      ) : (
                        <span className="block w-4" />
                      )}
                    </td>
                  )}
                  {columns.map((column) => {
                    const isNumeric = ['number', 'currency', 'decimal', 'percent'].includes(column.format);
                    return (
                      <td key={column.key} className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900 ${!column.render ? 'whitespace-nowrap' : ''} ${isNumeric ? 'text-right' : ''}`}>
                        {column.render ? column.render(row[column.key], row) : formatValue(row[column.key], column.format)}
                      </td>
                    );
                  })}
                </tr>
                {expanded && (
                  <tr className="bg-gray-50/70">
                    <td colSpan={columns.length + (hasExpandableRows ? 1 : 0)} className="px-4 sm:px-6 py-4">
                      {renderExpandedContent(row)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      {/* Mobile: toggle button + collapsible table */}
      <div className="md:hidden">
        <button
          onClick={() => setMobileExpanded(!mobileExpanded)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Table2 className="w-4 h-4 text-gray-500" />
            {mobileExpanded ? 'Hide Details' : 'Show Details'}
            <span className="text-xs text-gray-400">({data.length} rows)</span>
          </span>
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${mobileExpanded ? 'rotate-90' : ''}`} />
        </button>
        {mobileExpanded && <div className="mt-2">{table}</div>}
      </div>

      {/* Desktop: always visible */}
      <div className="hidden md:block">
        {table}
      </div>
    </>
  );
}
